/**
 * SendMessageDialog — Hinge/Bumble/Tinder-style bottom-sheet composer.
 *
 * Mobile parity (Aug 2026 spec):
 *   • Slide-up sheet with grabber pill + 24 px top corners
 *   • Recipient reference card (left accent + name + 44 px photo thumbnail)
 *   • Sender avatar next to a borderless multi-line note input
 *   • Char count under input (turns red near cap)
 *   • Footer: N / cap-left pill + solid-black paper-plane send button
 *   • Success flash on send (green + check for ~900 ms) before dismiss
 *   • Free-tier subtle upsell link at bottom (no loud banner)
 *   • NEVER surfaces "Unlimited" — always shows N / cap remaining today
 *
 * Business rules (Feb 2026): Free 0 · Premium 3 · VIP 5. Free users must
 * purchase Warm Intros to send (backend spends purchased_intros as overflow).
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Send, Check, X, Loader2 } from 'lucide-react';

const MAX_LEN = 250;

// Client-side source of truth for caps. Mirrors backend intros.py.
// Backend enforces; client displays. Never render "Unlimited".
const TIER_CAP = { free: 0, premium: 3, vip: 5 };

export default function SendMessageDialog({ open, onOpenChange, target, onSent }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [text, setText] = useState('');
  const [limits, setLimits] = useState(null);
  const [sending, setSending] = useState(false);
  const [flashOk, setFlashOk] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
      setFlashOk(false);
      api.get('/intros/limits').then((r) => setLimits(r.data)).catch(() => {});
    }
  }, [open]);

  // Escape-key close (parity with mobile spec)
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onOpenChange(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const tier = limits?.tier || 'free';
  const cap = TIER_CAP[tier] ?? 0;
  const usedToday = limits?.used_today ?? 0;
  const remaining = Math.max(0, cap - usedToday);
  const purchased = limits?.purchased_intros ?? 0;
  // Only "at cap" if daily is spent AND no purchased intros stashed.
  // Backend spends purchased_intros as overflow so free/at-cap users can
  // still send when they have a warm-intro credit.
  const atCap = remaining === 0 && purchased === 0;

  const capLabel = useMemo(() => {
    if (!limits) return '';
    if (purchased > 0 && remaining === 0) {
      return `${purchased} intro${purchased === 1 ? '' : 's'} left`;
    }
    return `${remaining}/${cap} left today`;
  }, [limits, remaining, cap, purchased]);

  const send = async (e) => {
    e?.preventDefault();
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    try {
      const r = await api.post('/intros', {
        target_user_id: target.user_id,
        message: msg,
      });
      if (r.data.status === 'already_matched' && r.data.match_id) {
        toast.success("You're already matched — opening chat");
        onOpenChange(false);
        // Feb 2026 — even in the already-matched shortcut, advance the deck
        // so the user doesn't see the same profile again on the next tap.
        if (onSent) { try { onSent(); } catch {} }
        navigate(`/chat/${r.data.match_id}`);
        return;
      }
      // Success flash — green check for ~900 ms, then dismiss + toast.
      setFlashOk(true);
      setLimits((l) =>
        l ? { ...l, used_today: (l.used_today ?? 0) + 1, remaining: r.data.remaining_today } : l,
      );
      setTimeout(() => {
        toast.success(`Message sent to ${target?.display_name || 'them'}`);
        onOpenChange(false);
        // Feb 2026 — treat a sent intro as a right-swipe. Advance the swipe
        // deck to the next profile so users don't re-see someone they just
        // sent a message to. Callback is optional (dialog is used in other
        // places besides Discover).
        if (onSent) { try { onSent(); } catch {} }
      }, 900);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to send';
      toast.error(detail);
      if (err.response?.status === 429) {
        // Close first so the /store navigation doesn't race the exit anim.
        onOpenChange(false);
        setTimeout(() => navigate('/store'), 200);
      }
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const senderPhoto = user?.photo_url || user?.avatar_url;
  const targetPhoto = target?.photo_url;
  const targetName = target?.display_name || target?.name || 'them';
  const nearCap = text.length >= MAX_LEN - 20;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center"
      data-testid="send-message-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="send-message-title"
      onClick={() => onOpenChange(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 animate-in fade-in-0 duration-200" />

      {/* Sheet — slides up from bottom */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-md bg-white rounded-t-[24px] sm:rounded-[24px] sm:mb-8 pb-6 shadow-2xl animate-in slide-in-from-bottom duration-300"
      >
        {/* Grabber pill */}
        <div className="pt-2.5 pb-1 flex justify-center">
          <div className="w-10 h-1 rounded-full bg-black/15" />
        </div>

        {/* Header row */}
        <div className="px-5 pt-1 pb-3 flex items-center justify-center relative">
          <h2 id="send-message-title" className="font-brand text-[17px] font-[600] tracking-tight text-[#0F0F10]">
            Send a message
          </h2>
          <button
            onClick={() => onOpenChange(false)}
            data-testid="intro-close"
            aria-label="Close"
            className="absolute right-4 w-8 h-8 rounded-full bg-black/[0.05] hover:bg-black/[0.08] flex items-center justify-center text-[#0F0F10]"
          >
            <X size={15} />
          </button>
        </div>

        {/* Recipient reference card (Hinge-style left accent) */}
        <div className="mx-4 mb-3 rounded-2xl border border-[var(--hairline)] bg-[#FAFAFA] overflow-hidden" data-testid="intro-recipient-card">
          <div className="flex items-center gap-3 pl-3 pr-3 py-2.5 relative">
            <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#B91C1C] rounded-r-full" />
            <div className="flex-1">
              <p className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#8A8A93] leading-none mb-1">
                To
              </p>
              <p className="text-[15px] font-[600] text-[#0F0F10] tracking-[-0.2px]">
                {targetName}
              </p>
            </div>
            {targetPhoto ? (
              <img
                src={targetPhoto}
                alt=""
                className="w-11 h-11 rounded-full object-cover border border-white shadow-sm"
                data-testid="intro-recipient-photo"
              />
            ) : (
              <div className="w-11 h-11 rounded-full bg-black/[0.06]" />
            )}
          </div>
        </div>

        {/* Sender avatar + input */}
        <div className="px-4">
          <div className="flex gap-3">
            {senderPhoto ? (
              <img
                src={senderPhoto}
                alt=""
                className="w-9 h-9 rounded-full object-cover flex-shrink-0 mt-0.5"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-black/[0.06] flex-shrink-0 mt-0.5" />
            )}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
              maxLength={MAX_LEN}
              autoFocus
              rows={4}
              data-testid="intro-message-input"
              placeholder="Send your note…"
              className="flex-1 bg-transparent border-0 text-[15px] leading-snug text-[#0F0F10] placeholder:text-[#B8B8BF] focus:outline-none resize-none pt-1"
            />
          </div>

          {/* Char counter */}
          <div className="flex justify-end mt-0.5">
            <span
              className={`text-[11px] tabular-nums transition-colors ${
                nearCap ? 'text-[#B91C1C]' : 'text-[#B8B8BF]'
              }`}
            >
              {text.length}/{MAX_LEN}
            </span>
          </div>
        </div>

        {/* Footer: quota pill + send button */}
        <div className="px-4 pt-3 flex items-center justify-between gap-3">
          <div
            className={`inline-flex items-center h-11 px-4 rounded-full text-[12px] font-semibold ${
              atCap
                ? 'bg-[#FEE2E2] text-[#B91C1C]'
                : 'bg-black/[0.04] text-[#4B4B52]'
            }`}
            data-testid="intro-quota-pill"
          >
            {capLabel}
          </div>

          <button
            type="button"
            onClick={send}
            disabled={!text.trim() || sending || atCap || flashOk}
            data-testid="intro-send-btn"
            aria-label="Send"
            className={`w-[52px] h-[52px] rounded-full flex items-center justify-center transition-all shadow-md ${
              flashOk
                ? 'bg-[#15803D]'
                : atCap || !text.trim()
                ? 'bg-black/20 cursor-not-allowed'
                : 'bg-[#0F0F10] hover:scale-[1.04] active:scale-95'
            }`}
          >
            {flashOk ? (
              <Check size={20} className="text-white" strokeWidth={2.5} />
            ) : sending ? (
              <Loader2 size={18} className="animate-spin text-white" />
            ) : (
              <Send size={18} className="text-white" />
            )}
          </button>
        </div>

        {/* Free-tier subtle upsell */}
        {tier === 'free' && !flashOk && (
          <div className="px-5 pt-3 text-center">
            <button
              type="button"
              onClick={() => {
                onOpenChange(false);
                navigate('/subscription');
              }}
              data-testid="intro-upgrade-cta"
              className="text-[12px] text-[#8A8A93] hover:text-[#0F0F10] transition"
            >
              Upgrade to <span className="font-semibold gold-gradient-text">Premium</span> for 3 daily intros
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
